import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faPenLine } from '@fortawesome/pro-regular-svg-icons/faPenLine';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Space, theme, Typography } from 'antd';
import { PosTabs } from 'pos-client/components/shared/Layout/Tabs/PosTabs.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { PosPageContent } from 'pos-client/layouts/page/PosPageContent.tsx';
import { useActiveTab } from 'pos-client/tabs/useActiveTab.tsx';
import { createTabs } from 'pos-client/utils/createTabs.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { UpdateOpeningHoursGroupInputModel } from 'pos-shared/models/openingHoursGroupSchema.ts';

import { hoursGroupsTabs } from './HoursGroups/HoursGroupsTabs.ts';

export const OpeningHoursDetails = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { id } = useParams();
  const { data } = trpc.getOpeningHoursGroupById.useQuery(
    { id: id as string },
    {
      select: (groups) => {
        return groups.results.slice().shift();
      },
    }
  );
  const { getAllOpeningHoursGroups, getOpeningHoursGroupById } =
    trpc.useUtils();
  const openingHoursTabs = createTabs(hoursGroupsTabs, { data });
  const { activeTab, setActiveTab } = useActiveTab(openingHoursTabs);

  const { showSuccess, showError } = useNotifications();

  const navigate = useNavigate();

  const handleOk = async () => {
    await getAllOpeningHoursGroups.invalidate();
    await getOpeningHoursGroupById.invalidate();
  };

  const {
    mutateAsync: deleteOpeningHoursGroupsMutation,
    isLoading: isDeleting,
  } = trpc.deleteOpeningHoursGroup.useMutation({
    onSuccess: async () => {
      await getAllOpeningHoursGroups.invalidate();
      showSuccess(
        t('common.notification.success.message', {
          action: 'deleted',
          entity: 'opening hours group',
        })
      );
      navigate('/opening-hours/');
    },
    onError: async () => {
      showError(
        t('common.notification.error.message', {
          action: 'delete',
          entity: 'opening hours group',
        })
      );
    },
  });

  const { mutate: UpdateOpeningHoursGroupsMutation } =
    trpc.updateOpeningHoursGroup.useMutation({
      onSuccess: async () => {
        handleOk();
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'opening hours group',
          })
        );
      },
    });

  const { showModal: showDeleteConfirmation } = useConfirmModal({
    type: 'delete',
    title: `${t('common.buttons.remove')} - ${data?.groupName}`,
    content: t('locations.trading.deleteHoursGroupConfirm'),
    confirmText: t('locations.trading.deleteHoursGroup'),
    confirmProps: {
      ghost: true,
    },
    isLoading: isDeleting,
    async onConfirm() {
      await deleteOpeningHoursGroupsMutation({ id: id as string });
    },
  });

  const editGroupName = (newGroupName: string) => {
    if (!newGroupName) {
      showError(
        t('common.notification.error.message', {
          action: 'update',
          entity: 'opening hours group',
        })
      );
      return;
    }

    //(at least one) alphanumeric and spaces are allowed
    const validationRegex = /^(?=[a-zA-Z0-9])[a-zA-Z0-9\s]+$/;
    if (!newGroupName.match(validationRegex)) {
      showError(
        t('common.notification.error.message', {
          action: 'update',
          entity: 'opening hours group',
        })
      );
      return;
    }

    if (newGroupName !== data?.groupName) {
      const openingHoursInputModel: UpdateOpeningHoursGroupInputModel = {
        openingHoursId: id || '',
        data: {
          groupName: newGroupName,
          code: data?.code,
        },
      };

      const successMessage = `${t('locations.trading.newGroupNameIs')} ${newGroupName}`;

      showSuccess(successMessage);

      UpdateOpeningHoursGroupsMutation(openingHoursInputModel);
    }
  };

  const goTo = (tab: string) => {
    setActiveTab(tab);
    navigate(tab);
  };

  const goBack = () => {
    navigate('/settings/locations-administration/opening-hours-group');
  };

  return (
    <PosPageContent>
      <Col flex="auto">
        <PosTabs
          activeKey={activeTab}
          items={openingHoursTabs}
          onChange={(tab) => goTo(tab)}
          headerProps={{
            title: (
              <Typography.Title
                editable={{
                  icon: (
                    <FontAwesomeIcon
                      icon={faPenLine}
                      size="sm"
                      color={token.colorPrimary}
                    />
                  ),
                  onChange: (newGroupName) => {
                    editGroupName(newGroupName);
                  },
                  maxLength: 50,
                }}
                level={4}
              >
                {data?.groupName}
              </Typography.Title>
            ),
            actionComponent: (
              <Space>
                <Button
                  onClick={goBack}
                  icon={<FontAwesomeIcon icon={faArrowLeft} />}
                >
                  {t('common.buttons.back')}
                </Button>
                <Button
                  type="primary"
                  danger
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  onClick={() => showDeleteConfirmation()}
                  loading={isDeleting}
                >
                  {t('locations.trading.deleteHoursGroup')}
                </Button>
              </Space>
            ),
          }}
        />
      </Col>
    </PosPageContent>
  );
};
